import {
  Asset,
  AssetAttachmentCard,
  AssetKind,
  AttachmentCard,
  ImageMediumAttachmentCard,
  VideoMediumAttachmentCard,
} from 'types/Attachments';
import { AssetStatus, AssetStatusValue } from 'types/AssetStatus';
import { VIDEO_STATUS } from '@kontentino/kontentino-constants/Video';

export type CardMediaKind = 'image' | 'video' | 'gif' | 'document';

export type CardMediaSource =
  | 'legacy_medium'
  | 'legacy_video'
  | 'media_library_asset';

export type CardMediaStatus = 'ready' | 'preparing' | 'failed';

export type CardMedia = {
  kind: CardMediaKind;
  source: CardMediaSource;
  url: string | null;
  mimeType: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  customThumbnailUrl: string | null;
  status: CardMediaStatus;
  raw: Asset | ImageMediumAttachmentCard | VideoMediumAttachmentCard;
};

type GetCardMediaOptions = {
  lookupAsset?: (id: string) => Asset | undefined;
};

export function buildAssetLookup(
  cards: ReadonlyArray<AttachmentCard | AssetAttachmentCard>,
): (id: string) => Asset | undefined {
  const map = new Map<string, Asset>();
  for (const card of cards) {
    if ('asset' in card && card.asset) {
      map.set(card.asset.id, card.asset);
    }
  }
  return (id) => map.get(id);
}

/**
 * Whether an asset-shape card is still being prepared. Legacy media cards are
 * excluded on purpose: master lets a post with a still-processing legacy video
 * be scheduled, and accounts outside the Media Library rollout keep that.
 */
export function isAnyAssetCardPreparing(
  cards: ReadonlyArray<AttachmentCard | AssetAttachmentCard>,
): boolean {
  return cards.some(
    (card) => !card.medium && getCardMedia(card)?.status === 'preparing',
  );
}

export function getCardMedia(
  card: AttachmentCard | AssetAttachmentCard,
  options: GetCardMediaOptions = {},
): CardMedia | null {
  if (card.medium) {
    return getCardMediaFromLegacyMedium(card.medium);
  }
  if ('asset' in card && card.asset) {
    return getCardMediaFromAsset(
      card.asset,
      card.asset.custom_thumbnail_id,
      options,
    );
  }
  return null;
}

function getCardMediaFromLegacyMedium(
  medium: ImageMediumAttachmentCard | VideoMediumAttachmentCard,
): CardMedia {
  if (medium.type === 'image') {
    return {
      kind: 'image',
      source: 'legacy_medium',
      url: medium.sizes.original.src,
      mimeType: null,
      width: medium.sizes.original.width,
      height: medium.sizes.original.height,
      durationMs: null,
      customThumbnailUrl: medium.sizes.thumb.src,
      status: 'ready',
      raw: medium,
    };
  }
  const customThumbnailUrl =
    medium.custom_thumb?.src ?? medium.original_thumb?.src ?? null;
  return {
    kind: 'video',
    source: 'legacy_medium',
    url: medium.sizes.medium?.src ?? '',
    mimeType: medium.meta.mime_type || null,
    width: medium.sizes.medium?.width ?? null,
    height: medium.sizes.medium?.height ?? null,
    durationMs: medium.meta.duration ?? null,
    customThumbnailUrl,
    status: medium.status === VIDEO_STATUS.AVAILABLE ? 'ready' : 'preparing',
    raw: medium,
  };
}

function getCardMediaFromAsset(
  asset: Asset,
  customThumbnailId: string | null | undefined,
  options: GetCardMediaOptions,
): CardMedia {
  const kind = mapAssetToCardMediaKind(asset);
  const customThumbnailUrl = resolveCustomThumbnailUrl(
    asset,
    customThumbnailId,
    options,
  );
  const status = assetStatusToCardMediaStatus(asset.status);
  return {
    kind,
    source: 'media_library_asset',
    url: status === 'ready' ? asset.url : null,
    mimeType: asset.mime_type,
    width: asset.width ?? null,
    height: asset.height ?? null,
    durationMs: null,
    customThumbnailUrl,
    status,
    raw: asset,
  };
}

function assetStatusToCardMediaStatus(
  status: AssetStatusValue | null | undefined,
): CardMediaStatus {
  if (status === AssetStatus.READY) return 'ready';
  if (status === AssetStatus.FAILED) return 'failed';
  return 'preparing';
}

function mapAssetToCardMediaKind(asset: Asset): CardMediaKind {
  const explicit = (asset as Partial<Asset>).type;
  if (explicit) {
    return assetKindToCardMediaKind(explicit);
  }
  return mimeToCardMediaKind(asset.mime_type);
}

function assetKindToCardMediaKind(kind: AssetKind): CardMediaKind {
  if (kind === 'video') return 'video';
  if (kind === 'gif') return 'gif';
  if (kind === 'document') return 'document';
  return 'image';
}

function mimeToCardMediaKind(mime: string): CardMediaKind {
  if (mime === 'image/gif') return 'gif';
  if (mime === 'application/pdf') return 'document';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  return 'image';
}

function resolveCustomThumbnailUrl(
  asset: Asset,
  customThumbnailId: string | null | undefined,
  options: GetCardMediaOptions,
): string | null {
  if (customThumbnailId && options.lookupAsset) {
    const thumb = options.lookupAsset(customThumbnailId);
    if (thumb?.url) return thumb.url;
  }
  if (asset.poster_url) {
    return asset.poster_url;
  }
  return null;
}
